import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowLeftSLine from '@/assets/icons/ri_arrow_left_s_line.svg?component';
import ArrowRightSLine from '@/assets/icons/ri_arrow_right_s_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { useHorizontalOverflow } from '@/hooks/useHorizontalOverflow';

import styles from './PenComparisonTable.module.scss';

interface ScrollButtonProps {
    label: string;
    /** Whether the table already shows this side's edge. */
    atEdge: boolean;
    onScroll: () => void;
    children: ReactNode;
}

function ScrollButton(props: ScrollButtonProps) {
    const { label, atEdge, onScroll, children } = props;
    // aria-disabled rather than disabled: a disabled button drops out of the tab order, so
    // the focus of a keyboard user who scrolled to the end would fall back to the page.
    return (
        <Button
            variant="secondary"
            size="small"
            type="button"
            aria-label={label}
            title={label}
            aria-disabled={atEdge}
            onClick={atEdge ? undefined : onScroll}
            iconOnly>
            {children}
        </Button>
    );
}

interface PenTableScrollButtonsProps {
    /** The table's scroll region. */
    scroller: RefObject<HTMLElement | null>;
}

/**
 * Scrolls the pen table left and right while it is wider than its panel, so a keyboard
 * user reaches the columns it hides. Real buttons, shown only while the table overflows,
 * in place of a focusable scroll region (U9): that region would need a tabindex on a
 * non-interactive element, which the lint rules forbid.
 */
export function PenTableScrollButtons(props: PenTableScrollButtonsProps) {
    const { scroller } = props;
    const { t } = useTranslation();
    const overflow = useHorizontalOverflow(scroller);
    if (!overflow.overflows) return null;

    const scroll = (direction: -1 | 1) => {
        const element = scroller.current;
        if (!element) return;
        // Half the visible width, so a step never passes a column hidden under the pinned
        // Pen column; the stylesheet makes it smooth unless the reader asks for less motion.
        element.scrollBy({ left: (direction * element.clientWidth) / 2 });
    };

    return (
        <div className={styles.scrollButtons}>
            <ScrollButton
                label={t('feedingModule.table.scrollLeft')}
                atEdge={overflow.atStart}
                onScroll={() => scroll(-1)}>
                <SvgIcon
                    icon={ArrowLeftSLine}
                    size="inherit"
                    svgProps={{ 'aria-hidden': true, 'focusable': 'false' }}
                />
            </ScrollButton>
            <ScrollButton
                label={t('feedingModule.table.scrollRight')}
                atEdge={overflow.atEnd}
                onScroll={() => scroll(1)}>
                <SvgIcon
                    icon={ArrowRightSLine}
                    size="inherit"
                    svgProps={{ 'aria-hidden': true, 'focusable': 'false' }}
                />
            </ScrollButton>
        </div>
    );
}
